import workspaceAdminPermissionRegistry from "./workspace-admin-permissions.json";

const workspacePermissionDependencies = new Map(
    workspaceAdminPermissionRegistry.groups
        .flatMap((group) => group.permissions)
        .map((permission) => [permission.key, permission.dependencies || []]),
);

export function normalizePermissionList(value) {
    let permissions = value;

    if (typeof permissions === "string") {
        try {
            permissions = JSON.parse(permissions);
        } catch {
            permissions = [permissions];
        }
    }

    if (permissions && !Array.isArray(permissions) && typeof permissions === "object") {
        permissions = Object.entries(permissions)
            .filter(([, enabled]) => enabled === true)
            .map(([permission]) => permission);
    }

    return Array.isArray(permissions)
        ? permissions.map((permission) => String(permission || "").trim()).filter(Boolean)
        : [];
}

export function expandWorkspacePermissionDependencies(value) {
    const permissions = new Set(normalizePermissionList(value));
    let changed = true;
    while (changed) {
        changed = false;
        for (const permission of [...permissions]) {
            for (const dependency of workspacePermissionDependencies.get(permission) || []) {
                if (permissions.has(dependency)) continue;
                permissions.add(dependency);
                changed = true;
            }
        }
    }
    return [...permissions];
}

export function getWorkspaceRole(userRoles = []) {
    const workspaceRoles = userRoles.filter((role) => role?.scope_type === "workspace");
    return workspaceRoles.find((role) => role?.role === "owner")
        || workspaceRoles.find((role) => role?.role === "admin")
        || workspaceRoles.find((role) => role?.role === "viewer")
        || workspaceRoles.find((role) => role?.role === "guest")
        || null;
}

export function isWorkspaceOwner(value) {
    if (typeof value === "string") return value === "owner";
    if (value?.role) {
        return value.role === "owner"
            && (!value.scope_type || value.scope_type === "workspace");
    }
    return getWorkspaceRole(value?.user_roles || [])?.role === "owner";
}

export function resolveActiveWorkspaceUserRoles(
    workspaceMembership,
    graphMembership,
    graphUserRoles = [],
) {
    const workspaceMembershipKey = workspaceMembership?.documentId ?? workspaceMembership?.id;
    const graphMembershipKey = graphMembership?.documentId ?? graphMembership?.id;
    const graphMatchesActiveMembership = workspaceMembershipKey !== null
        && workspaceMembershipKey !== undefined
        && graphMembershipKey !== null
        && graphMembershipKey !== undefined
        && String(workspaceMembershipKey) === String(graphMembershipKey);

    const persistedRoles = workspaceMembership?.user_roles || [];
    if (!graphMatchesActiveMembership || !Array.isArray(graphUserRoles) || !graphUserRoles.length) {
        return persistedRoles;
    }

    const roleIdentity = (role) => {
        const persistedKey = role?.documentId || role?.id;
        return persistedKey
            ? `role:${persistedKey}`
            : `scope:${role?.scope_type || ""}:${role?.scope_id || ""}`;
    };
    const persistedByIdentity = new Map(persistedRoles.map((role) => [roleIdentity(role), role]));
    const merged = graphUserRoles.map((graphRole) => {
        const persistedRole = persistedByIdentity.get(roleIdentity(graphRole));
        if (!persistedRole) return graphRole;
        persistedByIdentity.delete(roleIdentity(graphRole));
        return {
            ...persistedRole,
            ...graphRole,
            permissions: graphRole?.permissions
                ?? (graphRole?.role === persistedRole?.role ? persistedRole?.permissions : undefined),
        };
    });

    return [...merged, ...persistedByIdentity.values()];
}

export function isWorkspaceReadOnlyRole(role) {
    const roleName = typeof role === "string" ? role : role?.role;
    return roleName === "viewer" || roleName === "guest";
}

export const WORKSPACE_PROJECT_MANAGEMENT_PERMISSIONS = new Set([
    "modify-project-settings",
    "archive-project",
    "canManageTasks",
    "upload-file",
]);

const WORKSPACE_ADMIN_PROJECT_PERMISSION_MAP = {
    "modify-project-settings": [
        "modify-project-settings",
        "manage-project-members",
        "share-project",
        // project meetings (docs/tasks/26-project-meetings.md; the server allows the same)
        "create-meeting",
        "edit-meeting",
        "delete-meeting",
    ],
    "archive-project": ["archive-project"],
    canManageTasks: [
        "create-task",
        "edit-task",
        "change-task-status",
        "assign-task",
        "delete-task",
        "create-milestone",
        "edit-milestone",
        "delete-milestone",
        "create-meeting",
        "edit-meeting",
        "delete-meeting",
    ],
    "upload-file": ["upload-file"],
};

export function hasWorkspaceProjectManagementPermission(userRoles = []) {
    const workspaceRole = Array.isArray(userRoles)
        ? getWorkspaceRole(userRoles)
        : userRoles;
    return workspaceRole?.role === "admin"
        && normalizePermissionList(workspaceRole.permissions)
            .some((permission) => WORKSPACE_PROJECT_MANAGEMENT_PERMISSIONS.has(permission));
}

export function hasWorkspaceWideProjectVisibility(userRoles = []) {
    const workspaceRole = Array.isArray(userRoles)
        ? getWorkspaceRole(userRoles)
        : userRoles;
    if (workspaceRole?.role === "owner") return true;
    return workspaceRole?.role === "admin"
        && normalizePermissionList(workspaceRole.permissions).includes("view-all-projects");
}

export function shouldBlockWorkspaceMutation(role) {
    // The client-side guard is only a UX optimization for known read-only roles.
    // When role data is missing or still being refreshed, let the backend remain
    // the authorization boundary instead of producing a false local denial.
    return isWorkspaceReadOnlyRole(role);
}

export function canManageWorkspaceSettings(role) {
    const roleName = typeof role === "string" ? role : role?.role;
    return roleName === "owner" || roleName === "admin";
}

const WORKSPACE_CONTEXT_INDEPENDENT_PATHS = new Set([
    "/workspace/create",
    "/mobile/workspace/create",
    "/payment/callback",
]);

export function isWorkspaceContextIndependentPath(pathname) {
    const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
    return WORKSPACE_CONTEXT_INDEPENDENT_PATHS.has(normalized);
}

export function canAccessWorkspacePage(role, page) {
    const roleName = typeof role === "string" ? role : role?.role;
    if (!roleName || !page) return false;

    if (isWorkspaceContextIndependentPath(page["nav-url"])) return true;

    if (roleName === "guest") {
        return (page.name === "goals" && page["nav-url"] === "/goals")
            || (page.name === "inbox" && page["nav-url"] === "/inbox");
    }

    if (page.name === "settings" || page["nav-url"] === "/settings") {
        // Every normal workspace member needs the profile tab for personal
        // account/session controls. Settings itself filters administrative tabs
        // by their individual permissions.
        return ["owner", "admin", "viewer"].includes(roleName);
    }

    if (!["owner", "admin", "viewer"].includes(roleName)) return false;
    if (!page.can || roleName === "owner" || roleName === "viewer") return true;
    if (roleName !== "admin") return false;

    return normalizePermissionList(role?.permissions).includes(page.can);
}

export function getEffectivePermissions(
    userRoles = [],
    scopeId,
    scopeRolePermissions = {},
    workspaceStatus = "active",
) {
    const workspaceRole = getWorkspaceRole(userRoles);
    if (workspaceStatus !== "active") return [];
    if (workspaceRole?.role === "owner") return ["*"];
    if (isWorkspaceReadOnlyRole(workspaceRole)) return [];

    const permissions = new Set(expandWorkspacePermissionDependencies(workspaceRole?.permissions));
    if (scopeId) {
        if (workspaceRole?.role === "admin") {
            for (const workspacePermission of [...permissions]) {
                for (const projectPermission of (
                    WORKSPACE_ADMIN_PROJECT_PERMISSION_MAP[workspacePermission] || []
                )) {
                    permissions.add(projectPermission);
                }
            }
        }

        const scopeRole = userRoles.find((role) => (
            role?.scope_type === "project"
            && String(role?.scope_id) === String(scopeId)
        ));
        const configuredPermissions = scopeRolePermissions?.[scopeRole?.role];
        const projectPermissions = configuredPermissions === undefined
            ? scopeRole?.permissions
            : configuredPermissions;
        for (const permission of normalizePermissionList(projectPermissions)) {
            permissions.add(permission);
        }
    }

    return [...permissions];
}
